import { fetch_question_for_edit } from '@/services/question_service';
import { extract_error_message } from '@/utils/api_response_helpers';
import { resolveFormOptionValues } from './create_sections_question_helpers.js';
import {
  applyQuestionTypeState,
  buildEditQuestionForm,
  buildNormalizedEditQuestionPayload,
  buildOptionInputsFromValues,
  extractQuestionOptionValues,
  resolveEditQuestionModalState,
} from './create_sections_question_form_helpers.js';
import {
  buildEditQuestionPayload,
  buildQuestionPayload,
  resolveSelectedQuestionType,
  serializeDependentQuestion,
} from './create_sections_question_form_payload_helpers.js';

function ensureMinimumOptionInputs(question) {
  if (question.show_option_inputs && question.option_inputs.length < 2) {
    question.option_inputs = ['', ''];
  }
}

function setEditQuestionSelectionState(ctx, section, question) {
  ctx.edit_question_error = false;
  ctx.active_edit_section_index = ctx.sections.indexOf(section);
  ctx.active_question_index = section?.questions?.indexOf(question) ?? null;
  ctx.current_edit_question = ctx.build_edit_question_form(question, ctx.active_question_index ?? 0);
  ctx.editing_question = true;
}

function hasValidEditQuestionTarget(section, question_id) {
  return Boolean(section && question_id);
}

function openEditQuestionModalState(ctx) {
  ctx.handle_question_type_change_for_edit(ctx.current_edit_question);
  ctx.edit_question_modal_open = true;
}

async function loadEditQuestionData(ctx, question_id) {
  const parsed_response = await fetch_question_for_edit(question_id);
  const question_data = parsed_response?.data || {};
  const has_question_data = Object.keys(question_data).length > 0;
  if (!has_question_data) {
    return;
  }

  const normalized_question = ctx.build_edit_question_form(
    question_data,
    ctx.active_question_index ?? 0
  );
  ctx.current_edit_question = buildNormalizedEditQuestionPayload(
    ctx,
    question_data,
    normalized_question,
    question_id
  );
  ensureMinimumOptionInputs(ctx.current_edit_question);
}

function setEditQuestionError(ctx, error) {
  ctx.show_error(extract_error_message(error, 'Failed to load question details.'));
}

function validateEditQuestionTarget(ctx, section, question_id) {
  if (hasValidEditQuestionTarget(section, question_id)) {
    return true;
  }
  ctx.show_error('Please select a valid question first.');
  return false;
}

async function withEditQuestionLoader(ctx, action) {
  const loader = await ctx.show_loader('Loading question...');
  try {
    await action();
  } finally {
    await ctx.close_loader(loader);
    ctx.editing_question = false;
  }
}

export const create_sections_question_form_methods = {
  handle_question_type_change_for_edit(form) {
    const selected_type = resolveSelectedQuestionType(this.question_types, form.question_type_id);
    applyQuestionTypeState(this, form, selected_type);
  },

  add_question_form() {
    this.question_forms.push(this.build_question_form(this.question_forms.length + 1));
  },

  remove_question_form(index) {
    if (this.question_forms.length > 1) this.question_forms.splice(index, 1);
  },

  handle_question_type_change_for_form(form) {
    const selected_type = resolveSelectedQuestionType(this.question_types, form.question_type_id);
    applyQuestionTypeState(this, form, selected_type);
  },

  add_option_input(form) {
    if (!Array.isArray(form.option_inputs)) form.option_inputs = [];
    form.option_inputs.push('');
  },

  remove_option_input(form, option_index) {
    if (Array.isArray(form.option_inputs) && form.option_inputs.length > 2) {
      form.option_inputs.splice(option_index, 1);
    }
  },

  get_trigger_options(form) {
    const trigger_options = form?.show_option_inputs
      ? resolveFormOptionValues(form.option_inputs)
      : [];
    return trigger_options;
  },

  serialize_dependent_questions(dependant_questions) {
    if (!Array.isArray(dependant_questions)) return [];
    return dependant_questions
      .map((dep_question) => serializeDependentQuestion(this, dep_question))
      .filter((dep) => dep.question_text && dep.question_type_id && dep.trigger_condition);
  },

  get_selected_question_raw_type(question_type_id) {
    const selected_type = resolveSelectedQuestionType(this.question_types, question_type_id);
    const selected_question_raw_type = String(selected_type?.question_type || selected_type?.type || selected_type?.formatted_type || '').toLowerCase();
    return selected_question_raw_type;
  },

  build_option_inputs_from_values(values = []) {
    return buildOptionInputsFromValues(this, values);
  },

  extract_question_option_values(question = {}) {
    return extractQuestionOptionValues(question);
  },

  build_edit_question_form(question = {}, index = 0) {
    return buildEditQuestionForm(question, index, this);
  },

  build_edit_question_payload() {
    return buildEditQuestionPayload(this);
  },

  build_question_payload(form, section_id) {
    return buildQuestionPayload(this, form, section_id);
  },

  async open_edit_question_modal(section_ref, question_ref) {
    const modal_state = resolveEditQuestionModalState(this, section_ref, question_ref);
    const { section, question, question_id } = modal_state;
    if (!validateEditQuestionTarget(this, section, question_id)) {
      return;
    }

    setEditQuestionSelectionState(this, section, question);
    try {
      await withEditQuestionLoader(this, async () => {
        await loadEditQuestionData(this, question_id);
        openEditQuestionModalState(this);
      });
    } catch (error) {
      setEditQuestionError(this, error);
    }
  },
};
